import {
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import "../../styles/speakReport.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL; 
// Tool Configuration
export const SpeakReportToolConfig = {
  name: "Speak Report",
  tool: "SpeakReportTool",
  customAction: null,
  icon: (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor">
      <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
      <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
    </svg>
  ),
};

// Report Sections Configuration
const REPORT_SECTIONS = [
  { key: "clinical_history", label: "Clinical History / Indication", rows: 3 },
  { key: "technique", label: "Technique", rows: 3 },
  { key: "findings", label: "Findings", rows: 8 },
  { key: "impression", label: "Impression / Conclusion", rows: 4 },
  { key: "recommendation", label: "Recommendation", rows: 3 },
];

// Status constants
const STATUS = {
  OPEN: "open",
  LOCKED: "locked",
  SIGNED_OFF: "signed_off",
};

const STATUS_LABELS = {
  open: "Open",
  locked: "Locked",
  signed_off: "Signed Off",
};

const STATUS_COLORS = {
  open: { bg: "#e8f5e9", color: "#2e7d32", border: "#a5d6a7" },
  locked: { bg: "#fff3e0", color: "#e65100", border: "#ffcc80" },
  signed_off: { bg: "#e3f2fd", color: "#0d47a1", border: "#90caf9" },
};

export default function SpeakReportPopup({ patient, onClose, onSaveToReportingTool }) {
  // States
  const [reportData, setReportData] = useState({
    clinical_history: "",
    technique: "",
    findings: "",
    impression: "",
    recommendation: "",
  });

  const [activeSection, setActiveSection] = useState("findings");
  const [isRecording, setIsRecording] = useState(false);
  const [statusMsg, setStatusMsg] = useState("Ready");
  const [speechSupported, setSpeechSupported] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [reportStatus, setReportStatus] = useState(STATUS.OPEN);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Refs
  const recognitionRef = useRef(null);
  const isRecordingRef = useRef(false);
  const finalTextRef = useRef("");

  // Helper function to extract study ID from patient object
  const getStudyId = (patient) => {
    if (patient?.study_id) return patient.study_id;
    if (patient?.studyId) return patient.studyId;
    if (patient?.study_instance_uid) return patient.study_instance_uid;
    if (patient?.StudyInstanceUID) return patient.StudyInstanceUID;

    if (patient?.studies && Array.isArray(patient.studies) && patient.studies.length > 0) {
      const firstStudy = patient.studies[0];
      return firstStudy?.study_id ||
             firstStudy?.studyId ||
             firstStudy?.id ||
             firstStudy?.study_instance_uid ||
             firstStudy?.StudyInstanceUID ||
             "N/A";
    }

    if (patient?.study) {
      return patient.study?.study_id ||
             patient.study?.studyId ||
             patient.study?.id ||
             patient.study?.study_instance_uid ||
             "N/A";
    }
    if (patient?.currentStudyId) return patient.currentStudyId;
    return "N/A";
  };

  const getStudyDescription = (patient) => {
    if (patient?.study_description) return patient.study_description;
    if (patient?.studyDescription) return patient.studyDescription;
    if (patient?.studies && Array.isArray(patient.studies) && patient.studies.length > 0) {
      const firstStudy = patient.studies[0];
      return firstStudy?.study_description ||
             firstStudy?.studyDescription ||
             firstStudy?.description ||
             "N/A";
    }

    if (patient?.study?.study_description) return patient.study.study_description;
    if (patient?.study?.description) return patient.study.description;

    return "N/A";
  };

  // Extract patient information
  const patientId = patient?.patient_id || patient?.patientId || patient?.id || "unknown";
  const patientName = patient?.patient_name || patient?.patientName || patient?.patient || "Unknown Patient";
  const studyId = getStudyId(patient);
  const studyDescription = getStudyDescription(patient);

  // Load existing report status from backend on mount
  useEffect(() => {
    if (studyId && studyId !== "N/A") {
      fetch(`${API_BASE_URL}/api/study-status/${studyId}/`)
        .then((res) => res.ok ? res.json() : null)
        .then((data) => {
          if (data?.report_status) {
            setReportStatus(data.report_status);
          }
          if (data?.report_data) {
            setReportData(data.report_data);
          }
        })
        .catch(() => {
          // Backend unavailable — start fresh with open status
        });
    }
  }, [studyId]);

  // Speech Recognition Setup
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setSpeechSupported(false);
      setStatusMsg("Speech Recognition not supported. Use Chrome.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";
    recognition.maxAlternatives = 5;

    recognition.onstart = () => {
      console.log("Speech Recognition Started");
      setStatusMsg(`🎤 Listening (${activeSection})`);
    };

    recognition.onresult = (event) => {
      let interimTranscript = "";
      let finalTranscript = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;

        if (event.results[i].isFinal) {
          finalTranscript += transcript + " ";
        } else {
          interimTranscript += transcript;
        }
      }

      if (finalTranscript) {
        finalTextRef.current += finalTranscript
          .replace(/full stop/gi, ".")
          .replace(/comma/gi, ",")
          .replace(/new line/gi, "\n")
          .replace(/next line/gi, "\n");
      }

      setReportData((prev) => ({
        ...prev,
        [activeSection]: finalTextRef.current + interimTranscript,
      }));
    };

    recognition.onerror = (event) => {
      console.error("Speech Recognition Error:", event.error);
      if (event.error === "aborted") return;
      setStatusMsg("⚠ " + event.error);
    };

    recognition.onend = () => {
      console.log("Speech Recognition Ended");
      if (!isRecordingRef.current) return;

      try {
        recognition.start();
      } catch (err) {
        console.log("Restart prevented");
      }
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognition) {
        recognition.stop();
        recognition.abort();
      }
    };
  }, [activeSection]);

  // Start Recording
  const startRecording = async (sectionKey) => {
    // Block recording if report is signed off
    if (reportStatus === STATUS.SIGNED_OFF) {
      setStatusMsg("⛔ Report is signed off. Cannot edit.");
      return;
    }

    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });

      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current.abort();
      }

      setActiveSection(sectionKey);
      finalTextRef.current = reportData[sectionKey] || "";
      setIsRecording(true);
      isRecordingRef.current = true;
      setStatusMsg(`🎤 Listening (${sectionKey})`);

      setTimeout(() => {
        try {
          recognitionRef.current?.start();
        } catch (err) {
          console.log("Already started");
        }
      }, 100);
    } catch (err) {
      console.error(err);
      setStatusMsg("⚠ Mic Permission Denied");
    }
  };

  // Stop Recording
  const stopRecording = useCallback(() => {
    console.log("Stopping Recording...");
    isRecordingRef.current = false;
    setIsRecording(false);
    setStatusMsg("🛑 Recording Stopped");

    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
        recognitionRef.current.abort();
      } catch (err) {
        console.log(err);
      }
    }
  }, []);

  // Handle Dictate Click
  const handleDictateClick = (sectionKey) => {
    if (reportStatus === STATUS.SIGNED_OFF) {
      setStatusMsg("⛔ Report is signed off. Cannot edit.");
      return;
    }

    if (isRecording && activeSection === sectionKey) {
      stopRecording();
      return;
    }

    if (isRecording) {
      stopRecording();
      setTimeout(() => startRecording(sectionKey), 500);
      return;
    }

    startRecording(sectionKey);
  };

  // ── Update report_status in backend ─────────────────────
  const updateStudyStatus = async (newStatus) => {
    if (studyId === "N/A") return true; // No study to update

    const res = await fetch(`${API_BASE_URL}/api/study-status/${studyId}/`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ report_status: newStatus }),
    });

    return res.ok;
  };

  // Save Report to Backend → locks report
  const saveReport = async () => {
    if (reportStatus === STATUS.SIGNED_OFF) {
      setStatusMsg("⛔ Signed-off reports cannot be re-saved.");
      return;
    }

    setIsSaving(true);
    setStatusMsg("💾 Saving report...");

    const reportToSave = {
      patientId,
      patientName,
      studyId,
      studyDescription,
      reportData,
      timestamp: new Date().toISOString(),
      reportType: "voice_report",
      report_status: STATUS.LOCKED,  // Save → lock
    };

    console.log("Saving report with:", { patientId, patientName, studyId, studyDescription });

    try {
      const response = await fetch(`${API_BASE_URL}/api/save-report/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(reportToSave),
      });

      if (response.ok) {
        const result = await response.json();
        console.log("Report saved successfully:", result);

        // Update study report_status to locked
        await updateStudyStatus(STATUS.LOCKED);
        setReportStatus(STATUS.LOCKED);

        setSaveSuccess(true);
        setStatusMsg("✅ Report saved and locked.");

        if (onSaveToReportingTool) onSaveToReportingTool(reportData);

        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        throw new Error("Failed to save report");
      }
    } catch (error) {
      console.error("Error saving report:", error);
      setStatusMsg("❌ Failed to save report");

      // Fallback: localStorage
      try {
        const localSave = {
          ...reportToSave,
          savedLocally: true,
          localTimestamp: Date.now(),
        };
        localStorage.setItem(`voice_report_${Date.now()}`, JSON.stringify(localSave));
        setReportStatus(STATUS.LOCKED);
        setStatusMsg("✅ Saved locally and locked (backend unavailable)");
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } catch (err) {
        console.error("Local save also failed:", err);
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Unlock Report (locked → open)
  const unlockReport = async () => {
    if (reportStatus !== STATUS.LOCKED) return;

    if (!window.confirm("Unlock report for editing?")) return;

    setIsUpdatingStatus(true);
    try {
      const ok = await updateStudyStatus(STATUS.OPEN);
      if (ok) {
        setReportStatus(STATUS.OPEN);
        setStatusMsg("🔓 Report unlocked for editing.");
      } else {
        // Fallback: allow local unlock anyway
        setReportStatus(STATUS.OPEN);
        setStatusMsg("🔓 Unlocked locally.");
      }
    } catch {
      setReportStatus(STATUS.OPEN);
      setStatusMsg("🔓 Unlocked locally.");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Sign Off Report (locked → signed_off)
  const signOffReport = async () => {
    if (reportStatus !== STATUS.LOCKED) {
      setStatusMsg("⚠ Save and lock the report before signing off.");
      return;
    }

    if (!window.confirm("Sign off this report? This action cannot be undone.")) return;

    setIsUpdatingStatus(true);
    setStatusMsg("✍ Signing off...");

    try {
      const ok = await updateStudyStatus(STATUS.SIGNED_OFF);
      if (ok) {
        setReportStatus(STATUS.SIGNED_OFF);
        setStatusMsg("✅ Report signed off.");
      } else {
        setReportStatus(STATUS.SIGNED_OFF);
        setStatusMsg("✅ Signed off locally.");
      }
    } catch {
      setReportStatus(STATUS.SIGNED_OFF);
      setStatusMsg("✅ Signed off locally.");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Clear All
  const clearAll = () => {
    if (reportStatus === STATUS.SIGNED_OFF) {
      setStatusMsg("⛔ Signed-off report cannot be cleared.");
      return;
    }
    if (reportStatus === STATUS.LOCKED) {
      setStatusMsg("⚠ Unlock the report before clearing.");
      return;
    }
    if (window.confirm("Clear complete report?")) {
      setReportData({
        clinical_history: "",
        technique: "",
        findings: "",
        impression: "",
        recommendation: "",
      });
    }
  };

  // Export Functions
  const exportPDF = () => {
    const reportHTML = buildReportHTML(patient, reportData, studyId, studyDescription, reportStatus);
    const win = window.open("", "_blank");
    win.document.write(`
      <html>
        <head>
          <title>MRI Report</title>
          ${reportStyle()}
        </head>
        <body>${reportHTML}</body>
      </html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 500);
  };

  const exportWord = () => {
    const reportHTML = buildReportHTML(patient, reportData, studyId, studyDescription, reportStatus);
    const htmlContent = `
      <html>
        <head>
          <meta charset="utf-8">
          ${reportStyle()}
        </head>
        <body>${reportHTML}</body>
      </html>
    `;
    const blob = new Blob([htmlContent], { type: "application/msword" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `MRI_Report_${patientId}_${studyId}.doc`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Derived flags
  const isLocked = reportStatus === STATUS.LOCKED;
  const isSignedOff = reportStatus === STATUS.SIGNED_OFF;
  const isEditable = reportStatus === STATUS.OPEN;
  const statusStyle = STATUS_COLORS[reportStatus] || STATUS_COLORS.open;

  // Render
  return createPortal(
    <div
      className="sr-overlay"
      onClick={(e) => {
        if (e.target.classList.contains("sr-overlay")) {
          stopRecording();
          onClose();
        }
      }}
    >
      <div className="sr-popup">
        {/* Header */}
        <div className="sr-header">
          <div className="sr-header-left">
            <span className="sr-title">MRI Voice Report</span>
            <span className={`ws-badge ${isRecording ? "ws-online" : "ws-offline"}`}>
              {isRecording ? "● Listening" : "● Idle"}
            </span>

            {/* ── Report Status Badge ── */}
            <span
              className="sr-report-status-badge"
              style={{
                background: statusStyle.bg,
                color: statusStyle.color,
                border: `1px solid ${statusStyle.border}`,
                borderRadius: "5px",
                padding: "2px 10px",
                fontSize: "12px",
                fontWeight: 600,
                letterSpacing: "0.4px",
                marginLeft: "8px",
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              {reportStatus === STATUS.OPEN && "🟢"}
              {reportStatus === STATUS.LOCKED && "🔒"}
              {reportStatus === STATUS.SIGNED_OFF && "✅"}
              {STATUS_LABELS[reportStatus]}
            </span>
          </div>

          <div className="sr-patient-badge">
            <span className="sr-patient-id">Patient ID: {patientId}</span>
            <span className="sr-patient-name">{patientName}</span>
            <span className="sr-study-id">Study ID: {studyId}</span>
          </div>

          <button
            className="sr-close-btn"
            onClick={() => {
              stopRecording();
              onClose();
            }}
          >
            ✕
          </button>
        </div>

        {/* Status Bar */}
        <div className={`sr-status-bar ${isRecording ? "listening" : "idle"}`}>
          <span>{statusMsg}</span>
          {saveSuccess && <span className="save-success-indicator">✓ Saved!</span>}
        </div>

        {/* Study Info */}
        {studyDescription !== "N/A" && (
          <div className="sr-study-info">📋 Study: {studyDescription}</div>
        )}

        {/* Locked Banner */}
        {isLocked && (
          <div
            className="sr-locked-banner"
            style={{
              background: "#fff8e1",
              borderLeft: "4px solid #ffa000",
              padding: "8px 14px",
              fontSize: "13px",
              color: "#5d4037",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            🔒 Report is locked. Click <strong>Unlock</strong> to make edits, or <strong>Sign Off</strong> to finalize.
          </div>
        )}

        {/* Signed Off Banner */}
        {isSignedOff && (
          <div
            className="sr-signeoff-banner"
            style={{
              background: "#e3f2fd",
              borderLeft: "4px solid #1565c0",
              padding: "8px 14px",
              fontSize: "13px",
              color: "#0d47a1",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            ✅ Report signed off by radiologist. This report is read-only.
          </div>
        )}

        {/* Speech Support Warning */}
        {!speechSupported && (
          <div className="sr-speech-warning">
            Speech Recognition not supported. Use Google Chrome.
          </div>
        )}

        {/* Report Sections */}
        <div className="sr-sections">
          {REPORT_SECTIONS.map((section) => {
            const isSectionActive = activeSection === section.key && isRecording;
            return (
              <div
                key={section.key}
                className={`sr-section ${isSectionActive ? "sr-section-active" : ""}`}
              >
                <div className="sr-section-header">
                  <label className="sr-section-label">{section.label}</label>
                  {/* Hide dictate button for signed-off reports */}
                  {!isSignedOff && (
                    <button
                      className={`sr-mic-btn ${isSectionActive ? "sr-mic-active" : ""}`}
                      onClick={() => handleDictateClick(section.key)}
                      disabled={isLocked}
                      title={isLocked ? "Unlock report to dictate" : ""}
                    >
                      {isSectionActive ? "■ Stop" : "🎤 Dictate"}
                    </button>
                  )}
                </div>
                <textarea
                  className="sr-textarea"
                  placeholder={`Speak or type ${section.label}`}
                  value={reportData[section.key]}
                  onChange={(e) =>
                    !isLocked && !isSignedOff &&
                    setReportData((prev) => ({ ...prev, [section.key]: e.target.value }))
                  }
                  rows={section.rows}
                  readOnly={isLocked || isSignedOff}
                  style={
                    isLocked || isSignedOff
                      ? { background: "#f5f5f5", color: "#777", cursor: "not-allowed" }
                      : {}
                  }
                />
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="sr-footer">
          {/* Clear All — hidden when signed off */}
          {!isSignedOff && (
            <button className="sr-clear-btn" onClick={clearAll} disabled={isLocked}>
              Clear All
            </button>
          )}

          {/* Save Report → locks on save; hidden when signed off */}
          {!isSignedOff && (
            <button
              className="sr-save-btn"
              onClick={saveReport}
              disabled={isSaving || isLocked}
              title={isLocked ? "Report is already saved and locked" : ""}
            >
              {isSaving ? "💾 Saving..." : "💾 Save Report"}
            </button>
          )}

          {/* Unlock — only visible when locked */}
          {isLocked && (
            <button
              className="sr-unlock-btn"
              onClick={unlockReport}
              disabled={isUpdatingStatus}
              style={{
                background: "#fff3e0",
                color: "#e65100",
                border: "1px solid #ffcc80",
                borderRadius: "6px",
                padding: "6px 14px",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "13px",
              }}
            >
              🔓 Unlock
            </button>
          )}

          {/* Export buttons — always visible */}
          <button className="sr-export-btn" onClick={exportPDF}>
            Export PDF
          </button>
          <button className="sr-export-btn" onClick={exportWord}>
            Export Word
          </button>

          {/* Sign Off — only visible to user when report is locked */}
          {isLocked && (
            <button
              className="sr-signoff-btn"
              onClick={signOffReport}
              disabled={isUpdatingStatus}
              style={{
                background: "#e3f2fd",
                color: "#0d47a1",
                border: "1px solid #90caf9",
                borderRadius: "6px",
                padding: "6px 16px",
                cursor: "pointer",
                fontWeight: 700,
                fontSize: "13px",
              }}
            >
              ✍ Sign Off
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// Helper Functions
function reportStyle() {
  return `
    <style>
      body {
        font-family: 'Segoe UI', Arial, sans-serif;
        padding: 40px;
        color: #2d3436;
        background: #f5f6fa;
      }
      .title {
        text-align: center;
        font-size: 28px;
        font-weight: bold;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        -webkit-background-clip: text;
        background-clip: text;
        color: transparent;
        border-bottom: 3px solid #00b894;
        padding-bottom: 10px;
        margin-bottom: 30px;
      }
      .patient-box {
        border: none;
        padding: 20px;
        margin-bottom: 30px;
        border-radius: 12px;
        background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
        box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      }
      .patient-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
      }
      .section {
        margin-bottom: 30px;
        background: white;
        padding: 20px;
        border-radius: 12px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.05);
      }
      .heading {
        background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
        padding: 12px;
        font-size: 18px;
        font-weight: bold;
        border-radius: 8px;
        margin-bottom: 15px;
        color: white;
      }
      .content {
        white-space: pre-wrap;
        line-height: 1.8;
        font-size: 15px;
        color: #2d3436;
      }
      .status-chip {
        display: inline-block;
        padding: 4px 14px;
        border-radius: 20px;
        font-size: 13px;
        font-weight: 600;
        margin-left: 12px;
        vertical-align: middle;
      }
      .status-open       { background: #e8f5e9; color: #2e7d32; border: 1px solid #a5d6a7; }
      .status-locked     { background: #fff3e0; color: #e65100; border: 1px solid #ffcc80; }
      .status-signed_off { background: #e3f2fd; color: #0d47a1; border: 1px solid #90caf9; }
      .signature-box {
        margin-top: 80px;
        text-align: right;
        padding: 20px;
        border-top: 2px solid #dfe6e9;
      }
    </style>
  `;
}

function buildReportHTML(patient, reportData, studyId, studyDescription, reportStatus) {
  const patientId = patient?.patient_id || patient?.patientId || patient?.id || "N/A";
  const patientName = patient?.patient_name || patient?.patientName || patient?.patient || "N/A";
  const resolvedStudyId = studyId || "N/A";
  const resolvedStudyDesc = studyDescription || "N/A";

  const statusLabels = {
    open: "Open",
    locked: "Locked",
    signed_off: "Signed Off",
  };
  const statusLabel = statusLabels[reportStatus] || "Open";

  return `
    <div class="title">
      MRI RADIOLOGY REPORT
      <span class="status-chip status-${reportStatus}">${statusLabel}</span>
    </div>
    <div class="patient-box">
      <div class="patient-grid">
        <div><b>Patient Name:</b> ${patientName}</div>
        <div><b>Patient ID:</b> ${patientId}</div>
        <div><b>Study ID:</b> ${resolvedStudyId}</div>
        <div><b>Study Description:</b> ${resolvedStudyDesc}</div>
        <div><b>Age / Gender:</b> ${patient?.age || "N/A"} / ${patient?.gender || "N/A"}</div>
        <div><b>Study Date:</b> ${new Date().toLocaleString("en-IN")}</div>
        <div><b>Modality:</b> MRI</div>
        <div><b>Report Status:</b> <span class="status-chip status-${reportStatus}">${statusLabel}</span></div>
      </div>
    </div>
    ${renderSection("Clinical History / Indication", reportData.clinical_history)}
    ${renderSection("Technique", reportData.technique)}
    ${renderSection("Findings", reportData.findings)}
    ${renderSection("Impression / Conclusion", reportData.impression)}
    ${renderSection("Recommendation", reportData.recommendation)}
    <div class="signature-box">
      <div>_____________________</div>
      <div>Radiologist Signature</div>
      ${reportStatus === "signed_off" ? `<div style="color:#0d47a1;font-weight:600;margin-top:6px;">✅ Digitally Signed Off — ${new Date().toLocaleString("en-IN")}</div>` : ""}
    </div>
  `;
}

function renderSection(title, content) {
  if (!content) return "";
  return `
    <div class="section">
      <div class="heading">${title}</div>
      <div class="content">${content}</div>
    </div>
  `;
}